import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionList, SelectOption, optionIdFor } from 'atoms/optionList/OptionList';
import styles from './SearchableSelect.module.css';

export type { SelectOption };

interface CommonProps {
  /** Plain text options, or { value, label, detail } when the text shown is not what the caller wants back. */
  options: (string | SelectOption)[];
  placeholder?: string;
  searchPlaceholder?: string;
  icon?: string;
  /** Show the selection but do not let it be changed. */
  disabled?: boolean;
  renderEmpty?: (searchTerm: string) => React.ReactNode;
}

interface SingleProps {
  multiple?: false;
  selected: string;
  onChange: (value: string) => void;
}

interface MultipleProps {
  /** Pick several: the panel stays open while picking, and an empty list means nothing is chosen. */
  multiple: true;
  selectedValues: string[];
  onChangeValues: (values: string[]) => void;
}

type SearchableSelectProps = CommonProps & (SingleProps | MultipleProps);

const toOption = (o: string | SelectOption): SelectOption => (typeof o === 'string' ? { value: o, label: o } : o);

// A dropdown with a built-in search box (on the option text, not its detail), for one choice or several. It is closed by
// default, showing the chosen option or the placeholder, and expands into a searchable list on click. Filtering by search
// term is handled internally; callers that need other filtering (e.g. by a related field) should narrow `options` first.
export const SearchableSelect = (props: SearchableSelectProps) => {
  const { placeholder, searchPlaceholder, icon, disabled = false, renderEmpty } = props;
  const { t } = useTranslation();
  const options = props.options.map(toOption);
  const picked = props.multiple ? props.selectedValues : props.selected ? [props.selected] : [];
  const labelOf = (value: string) => options.find((o) => o.value === value)?.label ?? value;
  const triggerLabel = picked.length > 1 ? t('FIELD.SELECTED_COUNT', { count: picked.length }) : labelOf(picked[0] ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Close on outside click, matching ThemeSelector's pattern.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()));

  // Opening highlights the current selection (or the first option).
  const open = () => {
    setActiveIndex(Math.max(options.findIndex((o) => o.value === picked[0]), 0));
    setIsOpen(true);
  };

  const close = (restoreFocus: boolean) => {
    setSearch('');
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const toggle = () => {
    if (disabled) return;
    if (isOpen) close(false);
    else open();
  };

  // Re-filtering highlights the first match.
  const updateSearch = (value: string) => {
    setSearch(value);
    setActiveIndex(0);
  };

  const handleSelect = (value: string) => {
    if (props.multiple) {
      props.onChangeValues(picked.includes(value) ? picked.filter((v) => v !== value) : [...picked, value]);
      return;
    }
    props.onChange(value);
    close(true);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      open();
    }
  };

  const moveActive = (step: number) => {
    if (filteredOptions.length === 0) return;
    setActiveIndex((i) => (i + step + filteredOptions.length) % filteredOptions.length);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const actions: Record<string, () => void> = {
      ArrowDown: () => moveActive(1),
      ArrowUp: () => moveActive(-1),
      Enter: () => { if (filteredOptions[activeIndex]) handleSelect(filteredOptions[activeIndex].value); },
      Escape: () => close(true),
    };
    const action = actions[event.key];
    if (action) {
      // Enter must not submit a surrounding form.
      event.preventDefault();
      action();
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div className={styles.dropdown} ref={containerRef}>
      <div
        ref={triggerRef}
        className={`${styles.trigger}${disabled ? ` ${styles.disabled}` : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
      >
        {icon && (
          <span className={`material-icons ${styles.triggerIcon}`} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={triggerLabel ? styles.triggerText : styles.placeholder}>
          {triggerLabel || placeholder || t('FIELD.SELECT_PLACEHOLDER')}
        </span>
        <span className={`material-icons ${styles.chevronIcon}`} aria-hidden="true">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </div>

      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.searchWrapper}>
            <span className={`material-icons ${styles.searchIcon}`} aria-hidden="true">search</span>
            <input
              autoFocus
              className={styles.searchInput}
              placeholder={searchPlaceholder ?? t('FIELD.SEARCH_PLACEHOLDER')}
              value={search}
              onChange={(e) => updateSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-controls={listId}
              aria-activedescendant={filteredOptions.length > 0 ? optionIdFor(listId, activeIndex) : undefined}
            />
            {search && (
              <span className={`material-icons ${styles.clearIcon}`} onClick={() => updateSearch('')}>
                close
              </span>
            )}
          </div>

          {filteredOptions.length > 0 ? (
            <OptionList
              options={filteredOptions}
              picked={picked}
              activeIndex={activeIndex}
              listId={listId}
              multiple={!!props.multiple}
              onSelect={handleSelect}
              onHover={setActiveIndex}
            />
          ) : (
            <div className={styles.empty}>
              {renderEmpty ? renderEmpty(search) : t('FIELD.NO_OPTIONS_FOUND')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
